import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { HttpRes } from '../../../models/common/http.model';
import { CommonServiceService } from '../common-service/common-service.service';

/** Minimum characters before a search request is issued. */
export const GLOBAL_SEARCH_MIN_CHARS = 3;
/** Debounce applied to keystrokes before hitting the API. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 300;
/** Page size requested from the videos endpoint. */
const GLOBAL_SEARCH_LIMIT = 12;

/**
 * One row as returned by `get_videos` for a search query.
 *
 * `HttpRes.data` is typed `any` by the shared HTTP model; this interface is the
 * typed adapter so `any` never reaches consuming components.
 */
export interface GlobalSearchRow {
  readonly id?: number | string;
  readonly event_id?: number | string;
  readonly title?: string;
  readonly event_name?: string;
  readonly filter_match_type?: string;
  readonly event_type?: string;
  readonly stream_status?: string;
  readonly streaming_platform?: string;
  readonly asset_playback_id?: string;
  readonly category_id?: number | string;
  readonly participant_type_id?: number | string;
}

/** A search hit, normalised for display in any layout's result list. */
export interface GlobalSearchResult {
  /** Event or video title. */
  readonly name: string;
  /** Human-readable match type ("Event", "Video", …). */
  readonly type: string;
  /** Currently live — drives the live badge. */
  readonly isLive: boolean;
  readonly id: number | string | undefined;
  readonly event_id: number | string | undefined;
  readonly stream_status: string | undefined;
  readonly streaming_platform: string | undefined;
  readonly asset_playback_id: string | undefined;
  /** Original row, needed for routing and for the video-view handoff. */
  readonly fullData: GlobalSearchRow;
}

/**
 * Global search behind the header (template 0) and the sidebar rail (template 1).
 *
 * Owns the query → results mapping and the "open this result" routing rules so
 * both layouts share one implementation instead of each carrying a copy.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly commonService = inject(CommonServiceService);
  private readonly router = inject(Router);

  /**
   * Runs a search against the live videos/events endpoint.
   *
   * Events are de-duplicated by `event_id`, preferring the broadcast (`B`)
   * variant when the same event appears twice — matching the existing header
   * behaviour exactly.
   *
   * @param query Raw user input.
   * @returns Normalised results; empty for queries under the minimum length.
   */
  search(query: string): Observable<GlobalSearchResult[]> {
    return this.commonService
      .get_videos({
        search: query,
        limit: GLOBAL_SEARCH_LIMIT,
        page_num: 1,
        object_type: null,
        event_id: null,
        object_id: null,
        status: null,
        participant_type: null,
        event_ids: null,
        category_ids: null,
        video_status: null,
        name: null,
        video_id: null,
      })
      .pipe(map((response: HttpRes) => this.toResults(response)));
  }

  /**
   * True when a query is long enough to search.
   *
   * @param query Raw user input.
   */
  isSearchable(query: string): boolean {
    return (query ?? '').trim().length >= GLOBAL_SEARCH_MIN_CHARS;
  }

  /**
   * Navigates to whatever a search hit represents (event, broadcast, live or
   * recorded stream) and stashes the row for the video view.
   *
   * @param result Result the user activated.
   */
  openResult(result: GlobalSearchResult): void {
    const row = result.fullData;

    if (globalThis.window?.sessionStorage) {
      sessionStorage.setItem('view_video', JSON.stringify(row));
    }

    if (row.filter_match_type === 'EVENT') {
      if (row.event_type === 'B') {
        void this.router.navigate([`broadcasts/${result.event_id}/0/0/1`]);
      } else {
        void this.router.navigate([
          `events/${result.event_id}/${row.category_id || 0}/${
            row.participant_type_id || 0
          }/5/5`,
        ]);
      }
      return;
    }

    void this.router.navigate([this.liveRouteFor(result)]);
  }

  /**
   * Picks the `live/<mode>/<id>` route for a non-event hit.
   *
   * `1` = watch live, `0` = watch recording, `-1` = neither available.
   *
   * @param result Result being opened.
   * @returns Router path for the stream.
   */
  private liveRouteFor(result: GlobalSearchResult): string {
    if (result.streaming_platform === 'IVS') {
      if (result.stream_status === 'L') {
        return `live/1/${result.id}`;
      }
      if (result.stream_status === 'S') {
        return `live/0/${result.id}`;
      }
      return `live/-1/${result.id}`;
    }

    if (result.stream_status === 'L') {
      return `live/1/${result.id}`;
    }
    if (result.stream_status === 'S' && result.asset_playback_id) {
      return `live/0/${result.id}`;
    }
    return `live/-1/${result.id}`;
  }

  /**
   * Narrows the untyped API payload and normalises it for display.
   *
   * @param response Raw `get_videos` response.
   * @returns Normalised results, or an empty list when the payload is unusable.
   */
  private toResults(response: HttpRes): GlobalSearchResult[] {
    const rows: unknown = response?.data;
    if (!Array.isArray(rows)) {
      return [];
    }

    const eventsById = new Map<number | string, GlobalSearchRow>();
    const others: GlobalSearchRow[] = [];

    for (const raw of rows) {
      if (typeof raw !== 'object' || raw === null) {
        continue;
      }
      const row = raw as GlobalSearchRow;

      if (row.filter_match_type !== 'EVENT') {
        others.push(row);
        continue;
      }

      const key = row.event_id ?? '';
      const existing = eventsById.get(key);
      // Prefer the broadcast variant when one event yields several rows.
      if (!existing || (row.event_type === 'B' && existing.event_type !== 'B')) {
        eventsById.set(key, row);
      }
    }

    return [...others, ...eventsById.values()].map((row) => ({
      name:
        row.filter_match_type === 'EVENT'
          ? row.event_name || 'Untitled Event'
          : row.title || 'Untitled Video',
      type: this.formatMatchType(row.filter_match_type),
      isLive: row.stream_status === 'L',
      id: row.filter_match_type === 'EVENT' ? row.event_id : row.id,
      event_id: row.event_id,
      stream_status: row.stream_status,
      streaming_platform: row.streaming_platform,
      asset_playback_id: row.asset_playback_id,
      fullData: row,
    }));
  }

  /**
   * Title-cases the API's match type for display.
   *
   * @param type Raw match type.
   * @returns Display label, defaulting to "Video".
   */
  private formatMatchType(type: string | undefined): string {
    if (!type) {
      return 'Video';
    }
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }
}
